import Box from "@mui/material/Box";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { ButtonGroup, Collapse, styled, Switch, useMediaQuery } from "@mui/material";
import Link from "next/link";
import styles from "./UserMenu.module.scss";
import { forwardRef, MouseEvent, useEffect, useState, type JSX } from "react";
import { useAtomValue, useAtom, useSetAtom } from "jotai";
import { textColorState, themeState, userDataState, userState, dividerState, paymentModalOpenState, paymentDrawerOpenState } from "@/lib/store";
import { Settings } from "@/models/auth";
import { updateUserSettings } from "@/controllers/auth";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import EngineeringIcon from "@mui/icons-material/Engineering";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import NotificationsIcon from "@mui/icons-material/Notifications";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import CodeIcon from "@mui/icons-material/Code";
import CandlestickChartIcon from "@mui/icons-material/CandlestickChart";
import { useTradingVisible } from "@/lib/trading/useTradingAccess";
import { StatusBar, Style } from "@capacitor/status-bar";
import LoginIcon from "@mui/icons-material/Login";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PaymentDrawer from "../payment-draweer/PaymentDrawer";
import { signOutFromBackend } from "@/lib/signOutFromBackend";
import { clearPendingPassword } from "@/lib/pendingPassword";
import { isOrtexApp } from "@/lib/utils";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";
import useOpenSubscriptionForm from "src/hooks/useOpenSubscriptionForm";
import HelpMenu from "../help/HelpMenu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import { ExpandMore } from "@mui/icons-material";

type Anchor = "right";
let themeTimeout: NodeJS.Timeout;

const isValidName = (name: string) => name && name !== "NotSet";

interface PropsTypes {
    firstName: string;
    lastName: string;
    isVisitor: boolean;
}

interface ListItem {
    href: string;
    primary: string;
    itemButtonClick?: () => void;
    listItemIcon: JSX.Element;
    condition?: boolean;
    handleClose: boolean;
    desktopOnly?: boolean;
    subMenu?: any[];
}

const AntSwitch = styled(Switch)(({ theme }) => ({
    "& .MuiSwitch-switchBase": {
        "& + .MuiSwitch-track": {
            opacity: 1,
            backgroundColor: "#337f79",
        },
        "&.Mui-checked": {
            color: "#fff",
        },
    },
}));

const upgradeButtonText = {
    "ORTEX Free": "Upgrade",
    "ORTEX Basic": "Get Advanced",
    "ORTEX Visitor": "Create free account",
};

const UserMenu = forwardRef<HTMLDivElement, PropsTypes>(({ firstName, lastName, isVisitor }, ref) => {
    const theme = useAtomValue(themeState);
    const textColor = useAtomValue(textColorState);
    const userData = useAtomValue(userDataState);
    const [user, setUser] = useAtom(userState);
    const dividerColor = useAtomValue(dividerState);

    const setPaymentModalOpen = useSetAtom(paymentModalOpenState);

    const { openSubscriptionForm } = useOpenSubscriptionForm();

    const isMobile = useMediaQuery("(max-width: 768px)");

    const [state, setState] = useState({
        right: false,
    });
    const setPaymentDrawerOpen = useSetAtom(paymentDrawerOpenState);
    const [checked, setChecked] = useState(false);
    const [subMenuOpen, setSubMenuOpen] = useState<{ [key: string]: boolean }>({});
    const tradingVisible = useTradingVisible();

    useEffect(() => {
        setChecked(theme === "dark");
    }, [theme]);

    const doLogin = () => {
        window.location.href = `${window.location.origin}/login`;
        toggleDrawer(anchor, false);
    };

    /* const doLogout = () => {
        logout().finally(() => (window.location.href = `${window.location.origin}/login`));
        toggleDrawer(anchor, false);
    }; */

    const doLogout = async () => {
        // An email-only sign-up may still run on its generated password: that secret never
        // outlives the session it was created in.
        clearPendingPassword();
        try {
            const ortexUser = localStorage.getItem("ortexUser");
            const vertexUser = localStorage.getItem("vertexUser");
            if (ortexUser) {
                await signOutFromBackend(vertexUser, ortexUser);
            }
            // Amplify loads on sign-out rather than with the header (src/lib/amplify.ts).
            const { signOut } = await import("@/lib/amplify");
            await signOut();
        } catch (error) {
            console.error("Error signing out user", error);
        } finally {
            Object.keys(localStorage).forEach((key) => {
                if (key.startsWith("CognitoIdentityServiceProvider") || key.startsWith("amplify-")) {
                    localStorage.removeItem(key);
                }
            });
            localStorage.removeItem("vertexUser");
            localStorage.removeItem("ortexUser");
            window.location.href = `${window.location.origin}/login`;
        }
    };

    const setStatusBarStyle = async (theme: "light" | "dark") => {
        const choosenStyle = theme === "light" ? Style.Light : Style.Dark;
        await StatusBar.setStyle({ style: choosenStyle }).catch((err) => {
            return;
        });
    };

    useEffect(() => {
        setStatusBarStyle(theme);
    }, [theme]);

    // Help dialog — driven by our own tutorials (Appcues replacement).
    const [helpOpen, setHelpOpen] = useState(false);
    const openHelp = () => {
        toggleDrawer(anchor, false);
        setHelpOpen(true);
    };

    const toggleDrawer = (anchor: Anchor, open: boolean) => (event: KeyboardEvent | MouseEvent) => {
        if (event && event.type === "keydown" && ((event as KeyboardEvent).key === "Tab" || (event as KeyboardEvent).key === "Shift")) {
            return;
        }

        setState({ ...state, [anchor]: open });
    };

    const onThemeChange = () => {
        const theme = checked ? "light" : "dark";
        localStorage.setItem("theme", theme);

        setStatusBarStyle(theme);

        const settings: Settings = { ...user.settings, theme };
        setUser({ ...user, settings });

        if (process.env.NEXT_PUBLIC_VERCEL_ENV && user?.subscription_plan !== "ORTEX Visitor") {
            clearTimeout(themeTimeout);
            themeTimeout = setTimeout(() => updateUserSettings(settings), 1000);
        }
        setChecked(!checked);
    };

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    const anchor = "right";

    const handleUpgrade = () => {
        trackUpgradeClick(UPGRADE_LOCATIONS.USER_MENU, { plan: user?.subscription_plan });
        if (isOrtexApp()) {
            setPaymentDrawerOpen(true);
        } else {
            setPaymentModalOpen(true);
            openSubscriptionForm();
        }
    };

    const toggleSubMenu = (index: number) => {
        setSubMenuOpen((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const listItems: ListItem[] = [
        {
            // Vertex trading: staff in the trading group, on a trading build only (src/lib/trading/access.ts).
            href: "/trading/stock-search",
            primary: "Trading",
            listItemIcon: (
                <ListItemIcon>
                    <CandlestickChartIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            handleClose: true,
            condition: tradingVisible,
            subMenu: [
                {
                    href: "/trading/stock-search",
                    primary: "Trade",
                    handleClose: true,
                },
                {
                    href: "/trading/portfolio-holdings",
                    primary: "Portfolio and Holdings",
                    handleClose: true,
                },
                {
                    href: "/trading/account-activities",
                    primary: "Account Activities",
                    handleClose: true,
                },
                {
                    href: "/trading/transfers-fundings",
                    primary: "Transfers and Funding",
                    handleClose: true,
                },
                {
                    href: "/trading/documents",
                    primary: "Documents",
                    handleClose: true,
                },
                {
                    href: "/trading/options-trading",
                    primary: "Options Trading",
                    handleClose: true,
                },
                {
                    // Withdrawal two-factor authentication (ortex-gui#1209).
                    href: "/trading/security",
                    primary: "Security",
                    handleClose: true,
                },
                {
                    href: "/trading/help-support",
                    primary: "Help and Support",
                    handleClose: true,
                },
                {
                    href: "/trading/agreements-disclosures",
                    primary: "Agreements and Disclosures",
                    handleClose: true,
                },
            ],
        },
        {
            href: user?.subscription_plan === "ORTEX Visitor" ? "/login?register=true" : "",
            primary: upgradeButtonText[user.subscription_plan],
            listItemIcon: (
                <ListItemIcon>
                    <WorkspacePremiumIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            itemButtonClick:
                user?.subscription_plan === "ORTEX Visitor"
                    ? null
                    : () => {
                          handleClose();
                          setTimeout(handleUpgrade, 100);
                      },
            condition: ["ORTEX Free", "ORTEX Basic", "ORTEX Visitor"].includes(user?.subscription_plan) && isMobile,
            handleClose: true,
        },
        {
            href: "/portfolio-admin",
            primary: "Edit Porfolios",
            listItemIcon: (
                <ListItemIcon>
                    <BusinessCenterIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            handleClose: true,
            condition: user.subscription_plan === "ORTEX Visitor",
        },
        {
            href: "",
            primary: "Help",
            itemButtonClick: () => openHelp(),
            listItemIcon: (
                <ListItemIcon>
                    <HelpCenterIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            handleClose: true,
        },
        {
            href: "/admin",
            primary: "Admin",
            listItemIcon: (
                <ListItemIcon>
                    <AdminPanelSettingsIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            condition: Boolean(userData && (userData?.is_staff || userData?.is_superuser)),
            handleClose: true,
        },
        {
            href: "",
            primary: "Staff tools",
            // Django-served staff hub (hosts the Trading Ideas returns
            // dashboard at /staff/trading-ideas, among others). Same
            // cross-host pattern as the API Documentation entry below:
            // NEXT_PUBLIC_API_URL is the Django host.
            itemButtonClick: () => {
                window.open(`${process.env.NEXT_PUBLIC_API_URL}/staff`, "_blank");
            },
            listItemIcon: (
                <ListItemIcon>
                    <EngineeringIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            condition: Boolean(userData && (userData?.is_staff || userData?.is_superuser)),
            handleClose: true,
        },
        {
            href: "/alerts",
            primary: "Alerts",
            listItemIcon: (
                <ListItemIcon>
                    <NotificationsIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            handleClose: true,
        },
        {
            href: "/backtester",
            primary: "Backtester",
            listItemIcon: (
                <ListItemIcon>
                    <QueryStatsIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            handleClose: true,
        },
        {
            href: "/apis",
            primary: "API",
            listItemIcon: (
                <ListItemIcon>
                    <CodeIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            handleClose: true,
            subMenu: [
                {
                    href: "/apis/plans",
                    primary: "Plans",
                    handleClose: true,
                },
                {
                    href: "/apis/credits",
                    primary: "API Keys and Credits",
                    handleClose: true,
                    condition: Boolean(userData && userData?.api_subscriber),
                },
                {
                    itemButtonClick: () => {
                        window.open(`${process.env.NEXT_PUBLIC_API_URL}/API/v1/docs/`, "_blank");
                    },
                    primary: "Documentation",
                    handleClose: true,
                },
            ],
        },
        {
            href: "/account",
            primary: "Account",
            listItemIcon: (
                <ListItemIcon>
                    <SettingsIcon style={{ color: textColor }} />
                </ListItemIcon>
            ),
            handleClose: true,
        },

        {
            href: "",
            primary: "Theme switch",
            itemButtonClick: () => {
                onThemeChange();
            },
            listItemIcon: (
                <>
                    <ListItemIcon className={styles.switch}>
                        <AntSwitch checked={checked} />
                    </ListItemIcon>
                </>
            ),
            handleClose: false,
        },
        {
            href: "/",
            primary: isVisitor ? "Login" : "Logout",
            itemButtonClick: () => {
                isVisitor ? doLogin() : doLogout();
            },
            listItemIcon: <ListItemIcon>{isVisitor ? <LoginIcon style={{ color: textColor }} /> : <LogoutIcon style={{ color: textColor }} />}</ListItemIcon>,
            handleClose: true,
        },
    ];

    const hasName = isValidName(firstName) && isValidName(lastName);

    return (
        <div>
            <HelpMenu open={helpOpen} onClose={() => setHelpOpen(false)} />
            <Box sx={{ alignSelf: "center" }}>
                {hasName ? (
                    <Button id="yes" aria-describedby={id} onClick={handleClick} sx={{ paddingY: 0, marginBottom: 0.4 }} className={styles["btn-user"]}>
                        <div style={{ color: textColor }}>
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", fontSize: "1.5rem" }} className={styles.userName}>
                                {`${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`}
                                <ExpandMoreIcon />
                            </Box>
                        </div>
                    </Button>
                ) : (
                    <Button id="no" aria-label="Account menu" aria-describedby={id} onClick={handleClick} sx={{ paddingY: 0.5, marginBottom: 0.4 }}>
                        <div style={{ color: textColor }} className={styles.userName}>
                            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                <AccountCircleIcon fontSize="large" />
                                <ExpandMoreIcon />
                            </Box>
                        </div>
                    </Button>
                )}
            </Box>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Typography sx={{ p: 2, paddingBottom: 0 }}>{hasName ? firstName + " " + lastName : ""} </Typography>
                <Typography variant="caption" sx={{ padding: 2 }}>
                    {user.subscription_plan}
                </Typography>
                {listItems
                    .filter((item) => (isOrtexApp() ? !item.desktopOnly : true))
                    .map((listItem, index) => {
                        if ((["Account", "Admin", "Edit Porfolios"].includes(listItem.primary) && user.subscription_plan === "ORTEX Visitor") || listItem.condition === false) {
                            return null;
                        } else if (listItem.subMenu) {
                            return (
                                <div key={index}>
                                    <List>
                                        <ListItem
                                            disablePadding
                                            // onClick={() => {
                                            //     listItem.handleClose && setTimeout(handleClose, 200);
                                            // }}
                                        >
                                            <ButtonGroup
                                                variant="text"
                                                sx={{
                                                    width: "100%",
                                                    "& .MuiButtonGroup-grouped:not(:last-of-type)": {
                                                        borderRight: 0,
                                                    },
                                                }}
                                            >
                                                <Link passHref legacyBehavior href={listItem.href} style={{ width: "calc(100% - 32px)" }}>
                                                    <ListItemButton>
                                                        {listItem.listItemIcon}
                                                        <ListItemText primary={listItem.primary} />
                                                    </ListItemButton>
                                                </Link>
                                                <ListItemButton
                                                    onClick={() => toggleSubMenu(index)}
                                                    sx={{
                                                        minWidth: "32px",
                                                        width: "32px",
                                                        padding: "6px",
                                                        borderLeft: `1px solid ${dividerColor}!important`,
                                                        justifyContent: "center",
                                                    }}
                                                >
                                                    {subMenuOpen[index] ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                </ListItemButton>
                                            </ButtonGroup>
                                        </ListItem>
                                        <Collapse in={subMenuOpen[index]} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {listItem.subMenu
                                                    .filter((subItem) => typeof subItem.condition === "undefined" || subItem.condition === true)
                                                    .map((subItem, subIndex) => (
                                                        <ListItem
                                                            key={subIndex}
                                                            disablePadding
                                                            onClick={() => {
                                                                subItem.handleClose && setTimeout(handleClose, 200);
                                                            }}
                                                        >
                                                            {subItem.itemButtonClick ? (
                                                                <ListItemButton sx={{ pl: 4 }} onClick={subItem?.itemButtonClick}>
                                                                    <ListItemText primary={subItem.primary} />
                                                                </ListItemButton>
                                                            ) : (
                                                                <Link passHref legacyBehavior href={subItem.href}>
                                                                    <ListItemButton sx={{ pl: 4 }} onClick={subItem?.itemButtonClick}>
                                                                        <ListItemText primary={subItem.primary} />
                                                                    </ListItemButton>
                                                                </Link>
                                                            )}
                                                        </ListItem>
                                                    ))}
                                            </List>
                                        </Collapse>
                                    </List>
                                    {index !== listItems.length - 1 && <Divider sx={{ backgroundColor: dividerColor }} />}
                                </div>
                            );
                        } else {
                            return (
                                <div key={index}>
                                    <List>
                                        <ListItem
                                            disablePadding
                                            onClick={() => {
                                                listItem.handleClose && setTimeout(handleClose, 200);
                                            }}
                                        >
                                            {listItem.itemButtonClick ? (
                                                <ListItemButton onClick={listItem?.itemButtonClick}>
                                                    {listItem.listItemIcon}
                                                    <ListItemText primary={listItem.primary} />
                                                </ListItemButton>
                                            ) : (
                                                <Link passHref legacyBehavior href={listItem.href}>
                                                    <ListItemButton onClick={listItem?.itemButtonClick}>
                                                        {listItem.listItemIcon}
                                                        <ListItemText primary={listItem.primary} />
                                                    </ListItemButton>
                                                </Link>
                                            )}
                                        </ListItem>
                                    </List>
                                    {index !== listItems.length - 1 && <Divider sx={{ backgroundColor: dividerColor }} />}
                                </div>
                            );
                        }
                    })}
            </Popover>
            <PaymentDrawer />
        </div>
    );
});

UserMenu.displayName = "UserMenu";

export default UserMenu;
